import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  useTheme,
  InputAdornment,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { tokens } from "../../theme";
/* Iconos */
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import BadgeIcon from "@mui/icons-material/Badge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../../components/Header";

const apiURL = process.env.REACT_APP_API_URLDB;

const BuscarSolicitud = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Estados
  const [formData, setFormData] = useState({ identidad: "" });
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = String(date.getUTCFullYear()).slice(0);
    return `${day}-${month}-${year}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResults(null);

    try {
      const response = await fetch(`${apiURL}/solicitud`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Error desconocido.");
      }

      setResults(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenDetail = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedSolicitud(null);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" p={2}>
      <Header title="Buscar Solicitudes" />

      {/* Formulario de Búsqueda */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          maxWidth: 400,
          mb: 4,
        }}
      >
        <TextField
          fullWidth
          variant="filled"
          type="text"
          color="secondary"
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{ color: `${colors.blueAccent[100]} !important` }}
              >
                <BadgeIcon />
              </InputAdornment>
            ),
          }}
          label="Número de Identidad"
          name="identidad"
          value={formData.identidad}
          onChange={handleChange}
          required
        />

        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: colors.blueAccent[400],
            color: colors.grey[900],
            borderRadius: "10px",
            height: "45px",
            fontWeight: "bold",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: colors.blueAccent[300],
              transform: "translateY(-2px)",
            },
          }}
        >
          Buscar
        </Button>
      </Box>

      {/* Mensaje de Error */}
      {error && (
        <Box mt={2}>
          <Typography
            color="error"
            variant="subtitle1"
            sx={{
              backgroundColor: "rgba(255, 0, 0, 0.1)",
              padding: "10px 20px",
              borderRadius: "8px",
              border: `1px solid ${theme.palette.error.main}`,
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {/* Listado de Resultados */}
      {results && (
        <Box width="100%" maxWidth="1000px">
          <Typography variant="h6" mb={2} color={colors.grey[100]}>
            Se encontraron {results.length} resultado(s)
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {results.map((sol) => (
              <Box
                key={sol.ID}
                sx={{
                  backgroundColor: colors.primary[400],
                  borderRadius: "12px",
                  padding: "16px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                  transition: "0.3s ease",
                  "&:hover": {
                    backgroundColor: colors.primary[300],
                    transform: "scale(1.01)",
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={colors.blueAccent[100]}
                  >
                    Solicitud: {sol.CodigoSolicitud}
                  </Typography>
                  <Typography color={colors.grey[100]}>
                    Estado: <strong>{sol.EstadoDescripcion}</strong>
                  </Typography>
                  <Typography color={colors.grey[100]}>
                    Fecha de Solicitud:{" "}
                    <strong>{formatDate(sol.SistemaFecha)}</strong>
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleOpenDetail(sol)}
                  sx={{
                    color: colors.grey[900],
                    backgroundColor: colors.blueAccent[400],
                    "&:hover": { backgroundColor: colors.blueAccent[300] },
                  }}
                >
                  Ver Detalles
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Modal de Detalles */}
      <Dialog
        open={openDetail}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.blueAccent[400],
            color: colors.grey[900],
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <DocumentScannerIcon sx={{ color: colors.blueAccent[100] }} />
            <Typography variant="h5" fontWeight="bold" color={colors.grey[900]}>
              Detalles de la Solicitud
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDetail}
            sx={{ color: colors.grey[900] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ borderTop: `1px solid ${colors.blueAccent[700]}` }}
        >
          {selectedSolicitud && (
            <Box>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }}
                gap={3}
                mt={2}
              >
                {/* Columna 1 */}
                <Box>
                  <Typography mb={1}>
                    <strong>Código:</strong> {selectedSolicitud.CodigoSolicitud}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Nombre Reclamante:</strong>{" "}
                    {selectedSolicitud.NombreReclamante}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Identidad:</strong> {selectedSolicitud.Identidad}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Correo:</strong> {selectedSolicitud.Correo}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Fecha Solicitud:</strong>{" "}
                    {formatDate(selectedSolicitud.SistemaFecha)}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Operador Denunciado:</strong>{" "}
                    {selectedSolicitud.Operador}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Tipo de Denuncia:</strong>{" "}
                    {selectedSolicitud.TipoDenunciaDescripcion || "Pendiente"}
                  </Typography>
                </Box>
                {/* Columna 2 */}
                <Box>
                  <Typography mb={1}>
                    <strong>Estado:</strong>{" "}
                    {selectedSolicitud.EstadoDescripcion}
                  </Typography>
                  <Typography mb={1}>
                    <strong>Fecha de Cierre:</strong>{" "}
                    {formatDate(selectedSolicitud.FechaCierre) || "Pendiente"}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" mt={2} mb={1}>
                    Descripción:
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.2)",
                      padding: "12px",
                      borderRadius: "8px",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <Typography variant="body2">
                      {selectedSolicitud.Descripcion}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box mt={4} textAlign="center">
                <Typography
                  variant="body2"
                  sx={{ color: colors.grey[900], fontStyle: "italic" }}
                >
                  Información actualizada el {new Date().toLocaleDateString()}.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button
            onClick={handleCloseDetail}
            variant="outlined"
            sx={{ color: colors.grey[900], borderColor: colors.grey[900] }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuscarSolicitud;
